/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {z} from '@hono/zod-openapi';

import {jsonValueSchema} from './AdvertisementPayloadSchema';

type WhenClauseInput = {
  path?: string;
  equals?: unknown;
  exists?: boolean;
  some?: WhenClauseInput[];
  every?: WhenClauseInput[];
};

export const whenClauseSchema: z.ZodType<WhenClauseInput> = z
  .lazy(() =>
    z
      .object({
        path: z.string().min(1).optional(),
        equals: jsonValueSchema.optional(),
        exists: z.boolean().optional(),
        some: z.array(whenClauseSchema).optional(),
        every: z.array(whenClauseSchema).optional(),
      })
      .strict()
  )
  .openapi({
    type: 'object',
    description: 'Recursive condition tree for tenant-specific advertising mapping overrides.',
    properties: {
      path: {type: 'string'},
      equals: {type: 'object', additionalProperties: true},
      exists: {type: 'boolean'},
      some: {type: 'array', items: {type: 'object', additionalProperties: true}},
      every: {type: 'array', items: {type: 'object', additionalProperties: true}},
    },
  });

export const tenantMappingOverrideModeSchema = z.enum(['replace', 'fallback', 'append']);
export const tenantMappingOverrideRequestModeSchema = z.enum(['replace', 'fallback']);
export const tenantMappingOverrideKindSchema = z.enum([
  'constant',
  'conditional-constant',
  'enum-remap',
]);
export const enumRemapValueSchema = z
  .object({
    remap: z.record(jsonValueSchema),
    defaultValue: jsonValueSchema.optional(),
  })
  .strict();

export const tenantMappingOverrideSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  platformCode: z.string().min(1),
  targetPath: z.string().min(1),
  priority: z.number().int(),
  mode: tenantMappingOverrideModeSchema,
  kind: tenantMappingOverrideKindSchema,
  value: jsonValueSchema,
  when: whenClauseSchema.nullable(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const overrideRuleFields = ['targetPath', 'kind', 'mode', 'value', 'when'] as const;

const createTenantMappingOverrideBaseRequestSchema = z
  .object({
    tenantId: z.string().uuid(),
    targetPath: z.string().min(1),
    kind: tenantMappingOverrideKindSchema,
    mode: tenantMappingOverrideRequestModeSchema,
    value: jsonValueSchema,
    when: whenClauseSchema.nullable().optional(),
    description: z.string().min(1),
  })
  .strict();

export const createTenantMappingOverrideRequestSchema =
  createTenantMappingOverrideBaseRequestSchema.superRefine((body, context) => {
    validateOverrideRule(body, context);
  });

export const updateTenantMappingOverrideRequestSchema = createTenantMappingOverrideBaseRequestSchema
  .partial()
  .required({tenantId: true})
  .extend({enabled: z.boolean().optional()})
  .strict()
  .superRefine((body, context) => {
    if (Object.keys(body).length === 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'PATCH body must include at least one field besides tenantId',
      });
    }

    if (overrideRuleFields.some((field) => body[field] !== undefined) && !body.description) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'description is required when rule fields change',
        path: ['description'],
      });
    }

    validateOverrideRule(body, context);
  });

export const reorderTenantMappingOverridesRequestSchema = z
  .object({
    tenantId: z.string().uuid(),
    overrideIds: z.array(z.string().uuid()).min(1),
  })
  .strict();

export type WhenClause = z.infer<typeof whenClauseSchema>;
export type TenantMappingOverrideDto = z.infer<typeof tenantMappingOverrideSchema>;
export type CreateTenantMappingOverrideRequest = z.infer<
  typeof createTenantMappingOverrideRequestSchema
>;
export type UpdateTenantMappingOverrideRequest = z.infer<
  typeof updateTenantMappingOverrideRequestSchema
>;

function validateOverrideRule(
  body: {
    kind?: z.infer<typeof tenantMappingOverrideKindSchema>;
    mode?: z.infer<typeof tenantMappingOverrideRequestModeSchema>;
    value?: unknown;
    when?: WhenClause | null;
  },
  context: z.RefinementCtx
): void {
  if (body.kind === 'constant' && body.when !== undefined && body.when !== null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'when must be null or omitted for constant overrides',
      path: ['when'],
    });
  }

  if (body.kind === 'conditional-constant' && body.when === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'when is required for conditional-constant overrides',
      path: ['when'],
    });
  }

  if (body.kind === 'conditional-constant' && body.when === null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'when must not be null for conditional-constant overrides',
      path: ['when'],
    });
  }

  if (body.kind === 'enum-remap' && body.mode === 'fallback') {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'fallback mode is not supported for enum-remap overrides',
      path: ['mode'],
    });
  }

  if (body.kind === 'enum-remap' && !enumRemapValueSchema.safeParse(body.value).success) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'enum-remap value must contain remap and optional defaultValue',
      path: ['value'],
    });
  }
}
